import { useState, useRef, useEffect } from "react";
import api from "@/lib/api";
import { Sparkle, X, PaperPlaneTilt } from "@phosphor-icons/react";

const SUGGESTIONS = [
  "Prépare une LOI type pour un cabinet à 550k€",
  "Quels risques surveiller en due diligence ?",
  "Génère 10 questions pour le dirigeant",
  "Prépare un plan d'intégration post-acquisition",
];

export default function CopilotPanel({ open, onClose, companyId = null, companyName = "" }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);
  const sessionId = useRef("copilot-" + Math.random().toString(36).slice(2));

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, loading]);

  const send = async (text) => {
    const msg = text || input;
    if (!msg.trim() || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setLoading(true);
    try {
      const { data } = await api.post("/ai/copilot", {
        message: msg, company_id: companyId, session_id: sessionId.current,
      });
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch {
      setMessages((m) => [...m, { role: "assistant", content: "Erreur lors de la génération." }]);
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="copilot-panel">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative w-full max-w-md bg-white border-l border-border flex flex-col h-screen fade-up">
        <div className="h-16 px-5 flex items-center justify-between border-b border-border ai-surface">
          <div className="flex items-center gap-2">
            <Sparkle size={20} weight="fill" className="text-[#6366f1]" />
            <div>
              <div className="font-head font-bold text-sm">IA Copilot</div>
              {companyName && <div className="overline text-muted-foreground">{companyName}</div>}
            </div>
          </div>
          <button onClick={onClose} data-testid="close-copilot-btn" className="text-muted-foreground hover:text-foreground"><X size={20} /></button>
        </div>

        <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-4">
          {messages.length === 0 && (
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground mb-3">Posez une question ou choisissez :</p>
              {SUGGESTIONS.map((s, i) => (
                <button key={s} onClick={() => send(s)} data-testid={`copilot-suggestion-${i}`}
                  className="w-full text-left text-sm px-3 py-2.5 border border-border hover:border-primary hover:bg-accent transition-colors">
                  {s}
                </button>
              ))}
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} className={`text-sm ${m.role === "user" ? "ml-8" : "mr-2"}`}>
              <div className="overline mb-1 text-muted-foreground">{m.role === "user" ? "Vous" : "Copilot"}</div>
              <div className={`px-3 py-2.5 whitespace-pre-wrap ${m.role === "user" ? "bg-primary text-white" : "ai-surface"}`}>
                {m.content}
              </div>
            </div>
          ))}
          {loading && <div className="text-sm text-muted-foreground mono animate-pulse">Le Copilot rédige…</div>}
        </div>

        <div className="p-3 border-t border-border">
          <div className="flex gap-2">
            <input data-testid="copilot-input" value={input} onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Demandez au Copilot…"
              className="flex-1 px-3 py-2.5 text-sm border border-border focus:border-primary outline-none" />
            <button onClick={() => send()} data-testid="copilot-send-btn" disabled={loading}
              className="px-3 bg-primary text-white hover:bg-blue-800 disabled:opacity-50">
              <PaperPlaneTilt size={18} weight="fill" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
